import React, { lazy, Suspense, useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useMedia } from 'react-use';
import Button from 'components/shared/button';
import styles from './styles.module.scss';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const SectionSlide = ({ section }) => {
  const ref = useRef(null);
  const wistiaPlayerRef = useRef();
  const [sectionRef, inView] = useInView({ threshold: 0.4 });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Use Framer Motion's useScroll to track the element's position in the viewport
  // "start end" means when the top of the element hits the bottom of the viewport
  // "center center" means when the center of the element hits the center of the viewport
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'center center'],
  });

  // Add spring smoothing to the scroll progress
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 70,
    damping: 20,
    restDelta: 0.001,
  });

  // Transform smooth progress to animation values
  // Original logic was 10% to 60% visibility.
  // Adjusted scale start from 0.8 to 0.9 for subtler effect.
  const scale = useTransform(smoothProgress, [0.1, 0.6], [0.8, 1]);

  // Parallax: Title moves faster (from further down), Content moves slower
  // Animation Timing:
  // - Title (top) animates early [0.1, 0.6]
  // - Content (bottom) animates late [0.55, 0.85] so it fades in when actually visible

  const titleY = useTransform(smoothProgress, [0.1, 0.6], [120, 0]);
  const titleOpacity = useTransform(smoothProgress, [0.1, 0.6], [0, 1]);

  const contentY = useTransform(smoothProgress, [0.1, 0.6], [40, 0]);
  const contentOpacity = useTransform(smoothProgress, [0.55, 0.85], [0, 1]);

  const isMobile = useMedia('(max-width: 768px)', false);

  const placeStyleToWistia = useCallback((wistiaElementParam) => {
    const wistiaElement = wistiaElementParam; // || wistiaPlayerRef.current;
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
          /* This CSS is now safely inside the Shadow DOM */
          video {
            
            object-fit: cover !important;
            
          }
         
          .click-for-sound-btn {
            display: none !important;
          }
      `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleApiReady = useCallback(
    (event) => {
      const player = event?.target;
      if (!player) return;
      // wistiaPlayerRef.current = player;
      placeStyleToWistia(player);
    },
    [placeStyleToWistia]
  );

  useEffect(() => {
    if (inView) {
      wistiaPlayerRef?.current?.play?.();
    } else {
      wistiaPlayerRef?.current?.pause?.();
    }
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      wistiaPlayerRef?.current?.pause?.();
    };
  }, [inView]);

  return (
    <div
      className={styles.slide}
      ref={(node) => {
        ref.current = node;
        sectionRef(node);
      }}
    >
      {/* Background Image/Video */}
      {section.image?.id && (
        <motion.div className={styles.backgroundImage} style={{ scale }}>
          <div
            className={styles.imageWrapper}
            id={section.videoId ? undefined : `fss-img-box-${section.image.id}`}
          >
            {isClient && section.videoId && (
              <Suspense fallback={null}>
                <WistiaPlayerLazy
                  mediaId={section.videoId}
                  ref={wistiaPlayerRef}
                  onApiReady={handleApiReady}
                  autoPlay={false}
                  playlistLoop
                  muted
                  className={styles.wistiaPlayer}
                  poster={false}
                  swatch={false}
                  controls={false}
                  resumable
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Suspense>
            )}
          </div>
        </motion.div>
      )}

      {/* Overlay Content */}
      <div className={styles.overlayContent}>
        {/* Title at top */}
        {section.title && (
          <motion.div
            className={styles.titleContainer}
            style={{ opacity: titleOpacity, y: titleY }}
          >
            <h2 className={styles.title}>{section.title}</h2>
          </motion.div>
        )}

        {/* Description and Button at bottom */}
        <div className={styles.bottomContent}>
          {section.description && (
            <motion.div
              className={styles.descriptionBox}
              style={{ opacity: contentOpacity, y: contentY }}
            >
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: section.description }}
              />
            </motion.div>
          )}
          {section.buttonLabel && section.buttonLink && (
            <motion.div
              className={styles.buttonContainer}
              style={{ opacity: contentOpacity, y: contentY }}
            >
              <Button
                to={section.buttonLink}
                size={isClient && isMobile ? 'sm' : 'lg'}
                theme="accent-primary"
              >
                {section.buttonLabel}
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

const FullScreenSections = ({ sections = [], theme = '' }) => {
  const containerRef = useRef(null);

  // Inject images once when sections arrive
  useEffect(() => {
    let timeoutId = null;

    if (sections && sections.length > 0 && containerRef?.current) {
      timeoutId = setTimeout(() => {
        sections.forEach((section) => {
          if (!section?.image?.id) return;
          const parent = containerRef?.current?.parentElement;
          const source = parent?.querySelector(`#fss-img-${section.image.id}`);
          const target = parent?.querySelector(`#fss-img-box-${section.image.id}`);

          if (source && target) {
            target.innerHTML = '';
            target.appendChild(source.cloneNode(true));
          }
        });
      }, 100);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [sections]);

  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <section
      ref={containerRef}
      className={cn(styles.wrapper, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      {sections.map((section, index) => (
        <SectionSlide key={section.id || index} section={section} theme={theme} />
      ))}
    </section>
  );
};

SectionSlide.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    description: PropTypes.string,
    buttonLabel: PropTypes.string,
    buttonLink: PropTypes.string,
    image: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    videoId: PropTypes.string,
  }).isRequired,
};

FullScreenSections.propTypes = {
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      title: PropTypes.string,
      label: PropTypes.string,
      description: PropTypes.string,
      buttonLabel: PropTypes.string,
      buttonLink: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    })
  ),
};

export default FullScreenSections;
